import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollAreaScrollbar, ScrollAreaViewport } from '@/components/ui/scroll-area';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { Search, X, Users, Building2, CheckSquare, Square } from 'lucide-react';

interface PeopleOrganizationFiltersProps {
  filters: TimesheetReportFilters;
  onFiltersChange: (filters: TimesheetReportFilters) => void;
  availableEmployees: Array<{ id: string; name: string; department: string }>;
  availableDepartments: string[];
}

export const PeopleOrganizationFilters: React.FC<PeopleOrganizationFiltersProps> = ({
  filters,
  onFiltersChange,
  availableEmployees,
  availableDepartments,
}) => {
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [departmentSearch, setDepartmentSearch] = useState('');

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    if (!employeeSearch) return availableEmployees;
    const searchLower = employeeSearch.toLowerCase();
    return availableEmployees.filter(
      (emp) =>
        emp.name.toLowerCase().includes(searchLower) ||
        emp.department.toLowerCase().includes(searchLower)
    );
  }, [availableEmployees, employeeSearch]);

  // Filter departments based on search
  const filteredDepartments = useMemo(() => {
    if (!departmentSearch) return availableDepartments;
    const searchLower = departmentSearch.toLowerCase();
    return availableDepartments.filter((dept) => dept.toLowerCase().includes(searchLower));
  }, [availableDepartments, departmentSearch]);

  // Group employees by department
  const employeesByDepartment = useMemo(() => {
    const grouped = new Map<string, typeof availableEmployees>();
    filteredEmployees.forEach((emp) => {
      const dept = emp.department;
      if (!grouped.has(dept)) {
        grouped.set(dept, []);
      }
      grouped.get(dept)!.push(emp);
    });
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredEmployees]);

  // Handle employee selection
  const handleEmployeeToggle = (employeeId: string) => {
    const currentEmployees = filters.employeeIds || [];
    const newEmployees = currentEmployees.includes(employeeId)
      ? currentEmployees.filter((id) => id !== employeeId)
      : [...currentEmployees, employeeId];

    onFiltersChange({
      ...filters,
      employeeIds: newEmployees.length > 0 ? newEmployees : undefined,
    });
  };

  // Handle department selection
  const handleDepartmentToggle = (department: string) => {
    const currentDepartments = filters.departments || [];
    const newDepartments = currentDepartments.includes(department)
      ? currentDepartments.filter((d) => d !== department)
      : [...currentDepartments, department];

    onFiltersChange({
      ...filters,
      departments: newDepartments.length > 0 ? newDepartments : undefined,
    });
  };

  // Bulk selection handlers
  const handleSelectAllEmployees = () => {
    onFiltersChange({
      ...filters,
      employeeIds: availableEmployees.map((emp) => emp.id),
    });
  };

  const handleClearAllEmployees = () => {
    onFiltersChange({
      ...filters,
      employeeIds: undefined,
    });
    setEmployeeSearch('');
  };

  const handleSelectAllDepartments = () => {
    onFiltersChange({
      ...filters,
      departments: availableDepartments,
    });
  };

  const handleClearAllDepartments = () => {
    onFiltersChange({
      ...filters,
      departments: undefined,
    });
    setDepartmentSearch('');
  };

  // Select all employees in a department
  const handleSelectDepartmentEmployees = (department: string) => {
    const deptEmployees = availableEmployees
      .filter((emp) => emp.department === department)
      .map((emp) => emp.id);
    const currentEmployees = filters.employeeIds || [];
    const newEmployees = Array.from(new Set([...currentEmployees, ...deptEmployees]));

    onFiltersChange({
      ...filters,
      employeeIds: newEmployees,
    });
  };

  const selectedEmployeeCount = filters.employeeIds?.length || 0;
  const selectedDepartmentCount = filters.departments?.length || 0;

  return (
    <div className="space-y-6">
      {/* Employees Filter */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Employees
              </CardTitle>
              <CardDescription>
                Filter by specific employees
                {selectedEmployeeCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedEmployeeCount} selected
                  </Badge>
                )}
              </CardDescription>
            </div>
            {selectedEmployeeCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllEmployees}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              value={employeeSearch}
              onChange={(e) => setEmployeeSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Bulk Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAllEmployees}
              className="flex-1 gap-2"
            >
              <CheckSquare className="h-4 w-4" />
              Select All ({availableEmployees.length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAllEmployees}
              className="flex-1 gap-2"
              disabled={selectedEmployeeCount === 0}
            >
              <Square className="h-4 w-4" />
              Clear Selection
            </Button>
          </div>

          {/* Employee List grouped by Department */}
          <ScrollArea className="h-[300px] max-h-[40vh] border rounded-md">
            <div className="p-4 space-y-4">
              {employeesByDepartment.length > 0 ? (
                employeesByDepartment.map(([department, employees]) => (
                  <div key={department} className="space-y-2">
                    {/* Department Header */}
                    <div className="flex items-center justify-between sticky top-0 bg-background py-2 border-b">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">{department}</span>
                        <Badge variant="outline" className="text-xs">
                          {employees.length}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSelectDepartmentEmployees(department)}
                        className="h-7 text-xs"
                      >
                        Select All
                      </Button>
                    </div>

                    {/* Employees in Department */}
                    <div className="space-y-2 pl-6">
                      {employees.map((emp) => {
                        const isChecked = filters.employeeIds?.includes(emp.id) || false;
                        return (
                          <div key={emp.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`employee-${emp.id}`}
                              checked={isChecked}
                              onCheckedChange={() => handleEmployeeToggle(emp.id)}
                            />
                            <Label
                              htmlFor={`employee-${emp.id}`}
                              className="text-sm font-normal cursor-pointer flex-1"
                            >
                              {emp.name}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No employees found matching "{employeeSearch}"
                </p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Departments Filter */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Departments
              </CardTitle>
              <CardDescription>
                Filter by departments
                {selectedDepartmentCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedDepartmentCount} selected
                  </Badge>
                )}
              </CardDescription>
            </div>
            {selectedDepartmentCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllDepartments}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search departments..."
              value={departmentSearch}
              onChange={(e) => setDepartmentSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Bulk Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAllDepartments}
              className="flex-1 gap-2"
            >
              <CheckSquare className="h-4 w-4" />
              Select All ({availableDepartments.length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAllDepartments}
              className="flex-1 gap-2"
              disabled={selectedDepartmentCount === 0}
            >
              <Square className="h-4 w-4" />
              Clear Selection
            </Button>
          </div>

          {/* Department List */}
          <ScrollArea className="h-[180px] max-h-[25vh] border rounded-md">
            <div className="p-4 space-y-2">
              {filteredDepartments.length > 0 ? (
                filteredDepartments.map((dept) => {
                  const isChecked = filters.departments?.includes(dept) || false;
                  const employeeCount = availableEmployees.filter(
                    (emp) => emp.department === dept
                  ).length;

                  return (
                    <div key={dept} className="flex items-center gap-2">
                      <Checkbox
                        id={`department-${dept}`}
                        checked={isChecked}
                        onCheckedChange={() => handleDepartmentToggle(dept)}
                      />
                      <Label
                        htmlFor={`department-${dept}`}
                        className="text-sm font-normal cursor-pointer flex-1 flex items-center justify-between"
                      >
                        <span>{dept}</span>
                        <Badge variant="outline" className="text-xs">
                          {employeeCount} {employeeCount === 1 ? 'employee' : 'employees'}
                        </Badge>
                      </Label>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No departments found matching "{departmentSearch}"
                </p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-4 rounded-md border border-blue-200 dark:border-blue-900">
        <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">💡 Tip</p>
        <p className="text-blue-800 dark:text-blue-200">
          You can filter by both employees and departments simultaneously. Department filters show
          all employees in those departments, while employee filters show specific individuals.
        </p>
      </div>
    </div>
  );
};
